import xhr from 'xhr';

/**
 * Bandeja de solicitudes de documentos (Ventanilla Klazia, fase 1). Reemplaza el Drive de solicitudes.
 * Endpoints en klazia-api: src/api/document-inbox. Roles: admin, supervisor_admin y secretary_admin.
 */

export const READ_ROLES = ['admin', 'supervisor_admin', 'secretary_admin'];
export const EDIT_ROLES = ['admin', 'supervisor_admin', 'secretary_admin'];

export const TYPE_LABEL = {
  constancia: 'Constancia',
  certificado: 'Certificado',
  paz_y_salvo: 'Paz y salvo',
  despacho: 'Despacho',
  anexo: 'Anexo',
  duplicado: 'Duplicado',
  simat: 'SIMAT',
  formulario: 'Formulario',
  otro: 'Otro',
};

export const KIND_LABEL = {
  principal: 'Principal (Anual y CLEI)',
  tecnicas: 'Principal técnicas',
  pension: 'Pensión (graduados)',
  otra: 'Otra',
};

export const STATUS = {
  nueva: { label: 'Nueva', tone: 'purple', color: '#574feb' },
  esperando_pago: { label: 'Esperando pago', tone: 'orange', color: '#F59E0B' },
  en_revision: { label: 'En revisión', tone: 'blue', color: '#3B82F6' },
  emitida: { label: 'Emitida', tone: 'green', color: '#10B981' },
  no_cumple: { label: 'No cumple', tone: 'red', color: '#EF4444' },
  abandonada: { label: 'Abandonada', tone: 'gray', color: '#94A3B8' },
};

// Estados que exigen motivo (la API también lo valida).
export const STATUSES_WITH_REASON = ['no_cumple', 'abandonada'];

export const CHANNEL_LABEL = {
  hibot: 'HiBot',
  whatsapp_convenios: 'WhatsApp convenios',
  correo: 'Correo',
  presencial: 'Presencial',
  simulador: 'Simulador',
  otro: 'Otro',
};

export const PRIORITY = {
  baja: { label: 'Baja', tone: 'gray' },
  media: { label: 'Media', tone: 'orange' },
  alta: { label: 'Alta', tone: 'red' },
};

export const PAYMENT = {
  pendiente: { label: 'Pendiente', tone: 'orange' },
  pagado: { label: 'Pagado', tone: 'green' },
  no_aplica: { label: 'No aplica', tone: 'gray' },
};

export const DELIVERY_LABEL = { digital: 'Digital (correo)', fisico: 'Físico (envío)' };

export const DEFAULT_AMOUNTS = { digital: 25000, fisico: 45000 };

// Nombres de los campos para el historial de cambios.
export const FIELD_LABEL = {
  type: 'Tipo',
  constancy_kind: 'Tipo de constancia',
  observations: 'Observaciones',
  requester_name: 'Nombre',
  requester_document: 'Documento',
  channel: 'Canal',
  phone: 'Celular',
  email: 'Correo',
  status: 'Estado',
  status_reason: 'Motivo',
  delivery_method: 'Método de envío',
  delivery_address: 'Dirección',
  delivery_neighborhood: 'Barrio',
  delivery_city: 'Ciudad',
  delivery_department: 'Departamento',
  delivery_phone: 'Teléfono de envío',
  priority: 'Prioridad',
  requested_at: 'Fecha de solicitud',
  delivered_at: 'Fecha de entrega',
  comments: 'Comentarios',
  amount: 'Valor',
  payment_status: 'Pago',
  student: 'Estudiante',
  assigned_to: 'Responsable',
};

const VALUE_LABELS = {
  type: TYPE_LABEL,
  constancy_kind: KIND_LABEL,
  channel: CHANNEL_LABEL,
  delivery_method: DELIVERY_LABEL,
  status: Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, v.label])),
  priority: Object.fromEntries(Object.entries(PRIORITY).map(([k, v]) => [k, v.label])),
  payment_status: Object.fromEntries(Object.entries(PAYMENT).map(([k, v]) => [k, v.label])),
};

export const errorMessage = (e) => e?.response?.data?.error?.message || 'No se pudo completar la acción.';

export const formatMoney = (v) => (v === null || v === undefined || v === '' ? '—' : `$${Number(v).toLocaleString('es-CO')}`);

export const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Bogota' });
};

export const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-CO', {
    day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Bogota',
  });
};

/** Fecha para un input type="date" (AAAA-MM-DD), en hora de Colombia. */
export const toDateInput = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
};

/** Días calendario desde la fecha de solicitud. */
export const daysSince = (d) => {
  if (!d) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000));
};

/** Valor de un cambio del historial, legible. */
export function formatChangeValue(field, value) {
  if (value === null || value === undefined || value === '') return 'vacío';
  if (VALUE_LABELS[field]) return VALUE_LABELS[field][value] || value;
  if (field === 'requested_at' || field === 'delivered_at') return formatDate(value);
  if (field === 'amount') return formatMoney(value);
  if (field === 'student') return `código ${value}`;
  return String(value);
}

export const documentLabel = (r) => {
  const type = TYPE_LABEL[r.type] || r.type;
  return r.type === 'constancia' && r.constancyKind ? `${type} · ${KIND_LABEL[r.constancyKind] || r.constancyKind}` : type;
};

function query(params) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== false) qs.append(k, v);
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export async function getInboxOptions() {
  const { data } = await xhr.get('/document-inbox/options');
  return data;
}

export async function getInboxSummary(params) {
  const { data } = await xhr.get(`/document-inbox/summary${query(params)}`);
  return data;
}

export async function getRequests(params) {
  const { data } = await xhr.get(`/document-inbox/requests${query(params)}`);
  return data;
}

export async function getRequest(id) {
  const { data } = await xhr.get(`/document-inbox/requests/${id}`);
  return data;
}

export async function createRequest(body) {
  const { data } = await xhr.post('/document-inbox/requests', body);
  return data;
}

export async function updateRequest(id, body) {
  const { data } = await xhr.put(`/document-inbox/requests/${id}`, body);
  return data;
}

export async function searchStudents(q) {
  const { data } = await xhr.get(`/document-inbox/students${query({ q })}`);
  return data?.data || [];
}

/* Constancia automática (fase 3) */

export const FIELD_SOURCE = {
  plataforma: { label: 'Plataforma', tone: 'blue' },
  regla: { label: 'Manual de documentos', tone: 'purple' },
  manual: { label: 'Escrito a mano', tone: 'orange' },
};

export async function getConstancy(id) {
  const { data } = await xhr.get(`/document-inbox/requests/${id}/constancy`);
  return data;
}

export async function previewConstancy(id, body) {
  const { data } = await xhr.post(`/document-inbox/requests/${id}/constancy/preview`, body);
  return data;
}

export async function saveConstancy(id, body) {
  const { data } = await xhr.put(`/document-inbox/requests/${id}/constancy`, body);
  return data;
}

/** Borrador en PDF (con marca de prueba). Devuelve un Blob. */
export async function constancyDraftPdf(id, body) {
  const { data } = await xhr.post(`/document-inbox/requests/${id}/constancy/pdf`, body, { responseType: 'blob' });
  return data;
}

/** Asigna el colegio en la ficha del estudiante desde la solicitud. */
export async function assignStudentSchool(id, school) {
  const { data } = await xhr.put(`/document-inbox/requests/${id}/student-school`, { school });
  return data;
}

export async function issueConstancy(id, body) {
  const { data } = await xhr.post(`/document-inbox/requests/${id}/constancy/issue`, body);
  return data;
}

/** Con responseType blob, el error del servidor llega como Blob: se lee para mostrar el mensaje. */
export async function blobErrorMessage(e) {
  const blob = e?.response?.data;
  if (blob instanceof Blob) {
    try {
      const json = JSON.parse(await blob.text());
      return json?.error?.message || 'No se pudo generar el borrador.';
    } catch {
      return 'No se pudo generar el borrador.';
    }
  }
  return errorMessage(e);
}
